import axios from 'axios';
import store from '@/store';
import qs from 'query-string';
import { debounce } from 'lodash';
import { ACCESS_TOKEN_NAME, REFRESH_TOKEN_NAME, SIGN_IN_PATH } from '@/common/constants/ConstCode';
import { common } from '@/common/service/apiList';
import { showTextModal } from '@/common/utils/ModalState';

const loading = () => {
  store.dispatch('layout/updateIsLoading', { isLoading: true });
};
const loaded = debounce(async () => {
  await store.dispatch('layout/updateIsLoading', { isLoading: false });
}, 500);

class ServiceExec {
  #serviceExec;
  #mock;
  constructor() {
    let axiosInstance = axios.create();
    axiosInstance.interceptors.request.use(this.handleRequest);
    axiosInstance.interceptors.response.use(this.handleSuccess, this.handleError.bind(this));
    this.#serviceExec = axiosInstance;
    // this.#mock = new mockAdapter(axiosInstance, { delayResponse: 2000 });
  }

  async handleRequest(request) {
    // 260513 사고 재발 방지: URL path segment에 undefined/null/NaN이 박힌 호출 차단.
    // HMR 재컴파일 중 Vuex stale state로 path variable이 JS undefined로 보내져
    // BE 권한 체크 fail / 변환 fail 일으킨 좋아요 전환 충전 사고 (feedback_hmr_stale_state.md).
    if (request.url) {
      const pathOnly = String(request.url).split('?')[0];
      if (/\/(undefined|null|NaN)(\/|$)/.test(pathOnly)) {
        const msg = '[ServiceExec] Aborted — path segment has undefined/null/NaN: ' + request.url;
        console.error(msg);
        const err = new Error(msg);
        err.code = 'URL_INVALID_PATH';
        return Promise.reject(err);
      }
    }

    request._spinnerTimeout = setTimeout(() => {
      loading();
      request._spinnerShown = true;
    }, 700);

    request.headers[ACCESS_TOKEN_NAME] = store.getters['user/getAccessToken'];
    request.headers[REFRESH_TOKEN_NAME] = store.getters['user/getRefreshToken'];
    request.headers['ROLE'] = store.getters['user/getRole'];

    return request;
  }
  async handleSuccess(response) {
    clearTimeout(response.config._spinnerTimeout);
    if (response.config._spinnerShown) {
      loaded();
    }
    return response;
  }

  async handleError(error) {
    const config = error.config || {};
    clearTimeout(config._spinnerTimeout);
    if (config._spinnerShown) {
      loaded();
    }

    // 인증 만료 처리 (실제 401 응답만 처리, 메인/로그인 페이지에서는 hard reload 스킵)
    // 260520: 카카오톡 공유 링크로 /signin 진입 시 background API 401이 떨어지면
    // location.href=/signin reload → 같은 API 또 401 → 무한 리다이렉트 루프가 생겼다.
    // 이미 signin 경로에 있으면 redirect를 스킵해 루프를 끊는다. signOut(토큰 클리어)은 그대로 실행.
    if (error?.response?.status === this.status.UNAUTHORIZED) {
      // 260618 (B) fire-and-forget 분석/추적 호출(/api/retention/track/*)의 401은 세션에 손대지 않는다.
      //   retentionTracker.js는 스스로 "분석 데이터라 본 비즈니스 흐름엔 영향 없음"을 약속하는데,
      //   이 전역 핸들러의 signOut+redirect 부수효과가 그 약속을 깼다. 만료/stale 토큰 사용자가 공유 링크
      //   진입 시 session-open(인증필수·화이트리스트 제외) 401 → /signin 강제 이동 → "화면이 안 열림" 사고.
      const reqUrl = String(config.url || '').split('?')[0];
      if (/\/api\/retention\/track\//.test(reqUrl)) {
        return Promise.resolve(error.response);
      }

      // 260521 호크룩스 진단: location.href hard navigation은 history에 /signin push →
      // 카카오 in-app 브라우저에서 뒤로가기 시 layout 깨진 /signin 잔재로 떨어짐.
      // location.replace로 history 미생성 + /gate에서도 skip (카카오 link 진입점이라 401 정상).
      const pathname = (window.location.pathname || '').toLowerCase().replace(/\/+$/, '') || '/';
      const signinPath = SIGN_IN_PATH.toLowerCase();
      const onSignInPage = pathname === signinPath;
      const isMainPage = pathname === '/main';
      const isGatePage = pathname === '/gate';
      // 260618 (A) 공개 공유/딥링크 페이지(카톡 링크 진입점)는 background 401로 /signin 튕기지 않는다.
      //   기존 /main·/signin·/gate skip 의도를 공유 콘텐츠 경로(/business/.../share/*, ghost/detail)까지 확장.
      //   토큰은 signOut으로 정리하되, 페이지에서 내쫓지 않고 비로그인(게스트)로 머문다(share는 비로그인 열람 허용).
      const isPublicSharePage =
        /^\/business\/(?:[a-z-]+\/)?share(?:\/|$)/.test(pathname) || pathname === '/business/ghost/detail';
      if (!isMainPage && !onSignInPage && !isGatePage) {
        await store.dispatch('user/signOut');
        if (!isPublicSharePage) {
          setTimeout(() => {
            window.location.replace(SIGN_IN_PATH);
          }, 200);
        }
      }
    }

    // axios 에러를 그대로 반환 (기존 로직 유지)
    return Promise.resolve(error.response);
  }

  status = {
    OK: 200,
    NO_CONTENT: 204,
    ERROR: 500,
    NOT_FOUND: 404,
    UNAUTHORIZED: 401
  };

  nativeLogger(params = {}) {
    return this.get(common.nativeLog, { json: params });
  }
  get(path, params = {}) {
    return this.#serviceExec.request({
      method: 'GET',
      url: path,
      responseType: 'json',
      params: params,
      paramsSerializer: params =>
        qs.stringify(params, {
          arrayFormat: 'indices',
          skipNulls: true,
          allowDots: true
        })
    });
  }

  patch(path, payload) {
    return this.#serviceExec.request({
      method: 'PATCH',
      url: path,
      responseType: 'json',
      data: payload
    });
  }

  // 260430 5/1 풀스코프: If-Match etag 등 추가 헤더 필요 PATCH (NotificationSettingsV2)
  patchWithHeaders(path, payload, headers = {}) {
    return this.#serviceExec.request({
      method: 'PATCH',
      url: path,
      responseType: 'json',
      data: payload,
      headers
    });
  }

  post(path, payload) {
    return this.#serviceExec.request({
      method: 'POST',
      url: path,
      responseType: 'json',
      data: payload
    });
  }

  put(path, payload) {
    return this.#serviceExec.request({
      method: 'PUT',
      url: path,
      responseType: 'json',
      data: payload
    });
  }

  delete(path, payload) {
    return this.#serviceExec.request({
      method: 'DELETE',
      url: path,
      responseType: 'json',
      params: payload,
      paramsSerializer: params =>
        qs.stringify(params, {
          arrayFormat: 'indices',
          skipNulls: true,
          allowDots: true
        })
    });
  }
  multiPost(path, payload) {
    return this.#serviceExec.request({
      headers: { 'Content-Type': 'multipart/form-data' },
      method: 'POST',
      url: path,
      responseType: 'json',
      data: payload
    });
  }
  multiPut(path, payload) {
    return this.#serviceExec.request({
      headers: { 'Content-Type': 'multipart/form-data' },
      method: 'PUT',
      url: path,
      responseType: 'json',
      data: payload
    });
  }

  excel(path, payload = {}) {
    return this.#serviceExec.request({
      method: 'GET',
      url: `${path}?${qs.stringify(payload)}`,
      responseType: 'arraybuffer',
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; charset=UTF-8'
      }
    });
  }

  getMock() {
    return this.#mock;
  }

  // queryPost(path, payload) {
  //   payload = `${queryString.stringify(payload)}`;
  //   return this.ServiceExec.request({
  //     method: 'POST',
  //     url: path,
  //     responseType: 'json',
  //     data: payload
  //   });
  // }
}

let instance;
const initialize = () => {
  if (!instance) instance = new ServiceExec();
  return instance;
};
export default initialize();
